import { DownOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { AppButton } from '@components/AppComponents'
import TooltipInfo from '@components/TooltipInfo'
import { Button, Dropdown } from 'antd'
import { useEffect, useRef } from 'react'

import { ActivityHold } from '../../activity/activityTracker'
import {
  holdActivity,
  releaseHold,
  resumeInHold,
} from '../../activity/trackedRun'

import AppModal from '.'

type Props = {
  onCancel?: () => void
  modalParams: any
  hideWarningIcon?: boolean
  avoidCancelOnSubmit?: boolean
  btnYesText?: string
  btnNoText?: string
  btnCustomText?: string
  /**
   * The activity scope the modal's callbacks run in (appStore.openConfirmModal captures it when
   * the modal opens). Left undefined, the modal holds the scope open when it mounts and lets it go
   * when it unmounts; null runs the callbacks untracked.
   */
  activityHold?: ActivityHold | null
}
const ModalConfirm = ({
  onCancel = () => {},
  modalParams,
  hideWarningIcon = false,
  avoidCancelOnSubmit = false,
  btnYesText = 'Yes',
  btnNoText = 'No, return',
  btnCustomText = 'Custom',
  activityHold,
}: Props) => {
  const {
    message,
    onOk,
    functionCallbackCancel,
    footerCloseButton,
    modelWidth = 600,
    title = '',
    isShowWarningIcon = true,
    customButton,
    bodyStyle = {},
    functionCallbackCustomButton,
    btnYesTooltip = '',
    isUseDropDownButton = false,
  } = modalParams

  // A modal opened by the user's action (a confirm before a save) continues that action: its
  // callbacks run in the action's activity scope, so their requests join the same history entry.
  const ownHoldRef = useRef<ActivityHold | null>(null)
  useEffect(() => {
    if (activityHold !== undefined) {
      return undefined
    }
    const hold = holdActivity()
    ownHoldRef.current = hold
    return () => {
      ownHoldRef.current = null
      releaseHold(hold)
    }
  }, [activityHold])

  const runInActivity = (fn: () => unknown) =>
    resumeInHold(
      activityHold !== undefined ? activityHold : ownHoldRef.current,
      fn
    )

  const handleOnOkClick = () => {
    !avoidCancelOnSubmit && onCancel()
    onOk && runInActivity(() => onOk())
  }

  const handleCancel = () => {
    functionCallbackCancel && runInActivity(() => functionCallbackCancel())
    onCancel()
  }

  const handleCustomFunction = () => {
    functionCallbackCustomButton &&
      runInActivity(() => functionCallbackCustomButton())
    onCancel()
  }

  return (
    <AppModal
      title={title}
      open={true}
      width={modelWidth}
      bodyStyle={bodyStyle}
      className="custom-popup-confirm"
      footer={
        <>
          {customButton && customButton}
          {footerCloseButton || !onOk ? (
            <AppButton color="red" onClick={handleCancel}>
              Close
            </AppButton>
          ) : (
            <>
              {isUseDropDownButton ? (
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'btnYes',
                        label: modalParams?.btnYesText ?? btnYesText,
                        onClick: handleOnOkClick,
                      },
                      {
                        key: 'btnCustom',
                        label: modalParams?.btnCustomText ?? btnCustomText,
                        onClick: handleCustomFunction,
                      },
                    ],
                  }}
                >
                  <AppButton color="blue">
                    Save
                    <DownOutlined />
                  </AppButton>
                </Dropdown>
              ) : (
                <AppButton
                  color="blue"
                  className="w-[80px]"
                  onClick={handleOnOkClick}
                >
                  {modalParams?.btnYesText ?? btnYesText}
                  {modalParams?.btnYesTooltip && (
                    <TooltipInfo text={btnYesTooltip} />
                  )}
                </AppButton>
              )}

              <AppButton color="red" onClick={handleCancel}>
                {modalParams?.btnNoText ?? btnNoText}
              </AppButton>
            </>
          )}
        </>
      }
    >
      <div className="custom-content-popup-confirm flex items-center flex-col">
        {!hideWarningIcon && isShowWarningIcon && (
          <ExclamationCircleOutlined
            style={{ color: 'orange', fontSize: 55 }}
          />
        )}
        <div className="modal-confirm-message mt-5">{message}</div>
      </div>
    </AppModal>
  )
}

export default ModalConfirm
